import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { HistoryHelper } from 'services/HistoryHelper';
import { EntityVersion, EntityVersionType } from 'types';
import CircleButton from '../CircleButton';
import PencilSVG from '../svg/PencilSVG';
import { TabsListEmpty } from '../tabs/Tabs';
import EditVersionStatus from './EditVersionStatus';
import withEditContext, { HasEditContext } from './withEditContext';

type EditVersionProps = RouteComponentProps<any> & HasEditContext;

interface EditVersionState {
	confirmModalIsOpen: boolean;
	selectVersionmodalIsOpen: boolean;
	pendingVersionChange: EntityVersion | null;
	selectedVersionTypeId: number | null;
	isStatusPopoverOpen: boolean;
}

/**
 * Component that lists all available versions of an entity and allows switching the values currently
 * being edited to a specific previous version or Draft.
 *
 * @class EditVersion
 * @extends {React.Component<EditVersionProps, EditVersionState>}
 */
class EditVersion extends React.Component<EditVersionProps, EditVersionState> {
	constructor(props) {
		super(props);

		this.state = {
			confirmModalIsOpen: false,
			selectVersionmodalIsOpen: false,
			pendingVersionChange: null,
			selectedVersionTypeId: null,
			isStatusPopoverOpen: false,
		};
	}

	/**
	 * Toggles the visibility of the modal. Usually called to hide it.
	 *
	 * @memberof EditVersion
	 */
	public toggleConfirmModal = () => {
		this.setState({ confirmModalIsOpen: !this.state.confirmModalIsOpen });
	};

	/**
	 * Toggles the visibility of the select version modal.
	 *
	 * @memberof EditVersion
	 */
	public toggleSelectVersionModal = () => {
		this.setState({ selectVersionmodalIsOpen: !this.state.selectVersionmodalIsOpen });
	};

	/**
	 * After the user confirms that they want to see the other version, load the values from that version
	 * and close the modal.
	 *
	 * @private
	 * @memberof EditVersion
	 */
	private continueWithVersionChange = () => {
		const version = this.state.pendingVersionChange;
		if (!version) {
			// should never happen, so it'll happen all the time i'm sure.
			console.warn('Lost track of pending version change.');
			return;
		}

		console.debug('Attempting version change:', version.versionNo);
		this.props.history.replace({ search: version.id ? 'historyId=' + version.id : '' });
		this.props.editContext.load();

		this.toggleConfirmModal();
	};

	/**
	 * Event handler for the dropdown that contains all versions.
	 *
	 * @private
	 * @memberof EditVersion
	 */
	private handleVersionSelection = (version: EntityVersion) => {
		const selectedVersion = version;

		if (!selectedVersion) {
			// should never happen, so it'll happen all the time i'm sure.
			console.warn('Unable to find selected version?');
			return;
		}

		this.setState({
			pendingVersionChange: selectedVersion,
		});

		this.toggleConfirmModal();
	};

	/**
	 * Updates currently selected category
	 *
	 * @memberof EditVersion
	 */
	public handleCategoryClick = (versionType: number | null) => {
		this.setState({ selectedVersionTypeId: versionType });
	};

	/**
	 * Toggles the status popover
	 *
	 * @memberof EditVersion
	 */
	public toggleStatusPopover = () => {
		this.setState({ isStatusPopoverOpen: true });
	};

	public render() {
		const { selectedVersionTypeId } = this.state;

		// we're essentially binding the dropdown to a static value, which depends on the history value in the query string.
		const aggregateVersions = this.props.editContext.getAllVersions();

		const selectedVersion = this.props.editContext.getActiveVersion();

		const distinctVersionTypes = [
			...new Set([...aggregateVersions].map((aggregateVersion) => aggregateVersion.type)),
		].sort();

		let displayedVersions: EntityVersion[] = [];

		if (selectedVersionTypeId === null) {
			displayedVersions = aggregateVersions;
		} else {
			displayedVersions = aggregateVersions.filter((version) => {
				return version.type === selectedVersionTypeId;
			});
		}

		let btnText = '';

		if (selectedVersion) {
			btnText =
				'Version: ' + selectedVersion.versionNo + ' - ' + HistoryHelper.getVersionTypeName(selectedVersion);
		} else {
			btnText = 'Version: Current';
		}

		if (btnText.length > 30) {
			btnText = btnText.substring(0, 30) + '...';
		}

		const showStatus =
			!this.props.editContext.isCreateMode && !this.props.editContext.entityDefinition.hideStatusOnEdit;

		return (
			<div>
				<div className="version">
					<div className="version__header">
						<label className="version__header-label">Filter:</label>
						<select
							className="form-control"
							data-testid="version-filter"
							onChange={(e) => this.handleCategoryClick(e.target.value ? parseInt(e.target.value) : null)}
						>
							<option value="">All</option>
							{distinctVersionTypes.map((versionType, index) => (
								<option key={index} value={versionType}>
									{EntityVersionType[versionType]}
								</option>
							))}
						</select>
					</div>
					<div className="version__body">
						{displayedVersions.length === 0 ? (
							<TabsListEmpty message="No versions found in this category." />
						) : (
							<div className="roc-list roc-list--full-width roc-list--wide" role="table">
								<div className="roc-list__body" role="rowgroup">
									{displayedVersions.map((version, index) => {
										let isActive = false;

										if (selectedVersion) {
											if (version.id === selectedVersion.id) {
												isActive = true;
											}
										} else {
											if (version.type === 0) {
												isActive = true;
											}
										}

										const createdDate = HistoryHelper.getVersionTypeName(version, false);

										return (
											<div
												role="row"
												className="roc-list__row"
												key={index}
												data-testid={`version-item-${index}`}
											>
												{showStatus ? (
													<div className="roc-list__cell roc-list__cell--icon roc-list__cell--icon-versions">
														<EditVersionStatus version={version} index={index} />
													</div>
												) : null}

												<div role="cell" className="roc-list__cell roc-list__cell--label">
													{isActive ? (
														<>{createdDate} (Editing)</>
													) : (
														<button
															className="btn-link roc-list__cell-link"
															type="button"
															data-testid={`link-name-${index}`}
															onClick={() => this.handleVersionSelection(version)}
														>
															{createdDate}
														</button>
													)}
												</div>
												<div role="cell" className="roc-list__cell roc-list__cell--actions">
													{isActive ? null : (
														<CircleButton
															onClick={() => this.handleVersionSelection(version)}
															icon={<PencilSVG />}
															data-testid={`version-edit-${index}`}
														/>
													)}
												</div>
											</div>
										);
									})}
								</div>
							</div>
						)}
					</div>
				</div>

				<Modal isOpen={this.state.confirmModalIsOpen} toggle={this.toggleConfirmModal}>
					<ModalHeader toggle={this.toggleConfirmModal} data-testid="version-change-text">
						Version Change
					</ModalHeader>
					<ModalBody>
						You are about to start editing a different version of this entity. Any pending changes will be
						lost. Continue?
					</ModalBody>
					<ModalFooter>
						<Button color="primary-alt" outline onClick={this.toggleConfirmModal}>
							Cancel
						</Button>{' '}
						<Button
							color="primary"
							data-testid="btn-continue-version-change"
							onClick={this.continueWithVersionChange}
						>
							Continue
						</Button>
					</ModalFooter>
				</Modal>
			</div>
		);
	}
}

export default withRouter(withEditContext(EditVersion));
